/**
 * Iter310 — self-healing lazy() for chunk-load failures.
 *
 * Owner bug (Oct 2026, production only): "My page does not open. Only 3 dots."
 * The boot screen (`BootDots`) is the `<Suspense>` fallback. On production a
 * lazy route chunk was aborted mid-flight
 * (`/static/js/5154.a1a44896.chunk.js → net::ERR_ABORTED`, while the file
 * itself returns 200), which happens when a stale service worker intercepts
 * the request or a wrapped `window.fetch` throws. `React.lazy` CACHES that
 * rejection, so the fallback renders forever — the user is stuck on three
 * dots until a hard reload.
 *
 * This wrapper: retries the dynamic import (3 attempts, 400 ms backoff), and
 * if it still fails, purges the service worker + caches and reloads ONCE per
 * session so the user lands on a clean build instead of a dead screen.
 */
import { lazy, ComponentType } from 'react';

const RELOAD_FLAG = 'chunk_reload_attempted';

const looksLikeChunkError = (e: any): boolean => {
  const msg = String(e?.message || e || '');
  return (
    e?.name === 'ChunkLoadError' ||
    // NOTE: a bare "Failed to fetch" must NOT count — any ordinary API
    // network hiccup threw it, which nuked the SW + caches and reloaded the
    // page mid-boot (Iter312 production bug).
    /Loading chunk|Loading CSS chunk|dynamically imported module/i.test(msg)
  );
};

export async function recoverFromChunkError(): Promise<void> {
  let already = false;
  try {
    already = sessionStorage.getItem(RELOAD_FLAG) === '1';
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch { /* storage blocked — still try once */ }
  if (already) return;

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* noop */ }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* noop */ }
  window.location.reload();
}

export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const mod = await factory();
        try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* noop */ }
        return mod;
      } catch (e) {
        if (attempt === 2 || !looksLikeChunkError(e)) {
          if (looksLikeChunkError(e)) await recoverFromChunkError();
          throw e;
        }
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    throw new Error('unreachable');
  });
}

/** Catches chunk failures that happen outside a lazy() boundary. */
export function installChunkErrorRecovery(): void {
  const handler = (e: any) => {
    const err = e?.reason || e?.error || e;
    if (looksLikeChunkError(err)) recoverFromChunkError();
  };
  window.addEventListener('unhandledrejection', handler);
  window.addEventListener('error', handler);
}
